import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import cx from 'classnames'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { shallowEqual, useSelector } from 'react-redux'

import type { AppState } from '@/types'
import { Node } from '../Node'
import css from './Preview.module.scss'

const GAP = 18
const LIMIT = 19

type Props = {
  buttonRef: React.RefObject<HTMLDivElement | null>
  nodeId: string
  isOpen: boolean
}

const Preview: React.FC<Props> = ({ buttonRef, nodeId, isOpen }) => {
  const [domReady, setDomReady] = useState(false)

  const timeoutRef = useRef<number | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const treePreviews = useSelector(
    migrationProjectSelector.getProjectNodeTreePreviews(nodeId),
    shallowEqual
  )

  const scale = useSelector<AppState, number>(({ canvas }) => {
    const initialScale = 0.75
    const scaleFactor = initialScale * canvas.scale

    if (scaleFactor < 1) {
      return scaleFactor
    }

    return Math.floor(scaleFactor)
  })

  const positionToolbar = () => {
    if (!buttonRef.current || !panelRef.current) {
      return
    }

    const panelRect = panelRef.current.getBoundingClientRect()
    const buttonRect = buttonRef.current.getBoundingClientRect()

    const gap = GAP * scale

    const left = buttonRect.left - panelRect.width / 2 + buttonRect.width / 2
    const top = buttonRect.top + buttonRect.height + gap

    if (left + panelRect.width + gap > window.innerWidth) {
      panelRef.current.style.left = `${
        window.innerWidth - panelRect.width - gap
      }px`
    } else if (left < gap) {
      panelRef.current.style.left = `${gap}px`
    } else {
      panelRef.current.style.left = `${left}px`
    }

    panelRef.current.style.top = `${top}px`
  }

  const onCanvas = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = window.setTimeout(positionToolbar, 300)
  }

  useEffect(() => {
    setTimeout(positionToolbar)

    setDomReady(true)
  }, [])

  useEffect(() => {
    window.addEventListener('canvas', onCanvas)
    window.addEventListener('resize', onCanvas)

    return () => {
      window.removeEventListener('canvas', onCanvas)
      window.removeEventListener('resize', onCanvas)
    }
  })

  const transform = isOpen
    ? `translateY(0) scale(${scale})`
    : `translateY(16px) scale(${scale})`
  return domReady
    ? createPortal(
        <div
          className={cx(css.preview, {
            [css.useGrid]: treePreviews.length > 10,
            [css.show]: isOpen,
          })}
          style={{ transform }}
          ref={panelRef}
        >
          {treePreviews.slice(0, LIMIT).map((nodeId) => (
            <div className={css.node} key={nodeId}>
              <Node nodeId={nodeId} />
            </div>
          ))}
          {treePreviews.length > LIMIT && (
            <div>
              <div className={css.placeholder}>
                <div>{treePreviews.length - LIMIT}</div>
                <div>more</div>
              </div>
            </div>
          )}
        </div>,
        document.querySelector('#artboard') as HTMLElement
      )
    : null
}

export default Preview
